/**
 * Photoplan QC → Virtual Staging → Photoplan round-trip.
 * Captured from the inbound URL before App.tsx cleans query params.
 */

export const STAGING_PHOTOPLAN_RETURN_MESSAGE = 'photoplan-improve-return'

export interface PhotoplanReturnContext {
  bookingId: string
  assetId: string
  returnOrigin: string
  /** Customer gallery/order path. Absent for the existing staff QC flow. */
  returnPath?: string
}

const STORAGE_KEY = 'staging_photoplan_return'
const UUID = /^[0-9a-f-]{36}$/i

function allowedReturnOrigin(origin: string): boolean {
  if (origin === 'https://app.photoplan.ai') return true
  return /^http:\/\/localhost:\d+$/.test(origin)
}

export function readPhotoplanReturnContext(): PhotoplanReturnContext | null {
  const params = new URLSearchParams(window.location.search)
  const returnTo = params.get('returnTo')
  if (returnTo !== 'photoplan' && returnTo !== 'photoplan-gallery') return null
  const bookingId = params.get('bookingId')
  const assetId = params.get('assetId')
  const returnOrigin = params.get('returnOrigin')
  if (!bookingId || !assetId || !returnOrigin) return null
  if (!UUID.test(bookingId) || !UUID.test(assetId)) return null
  try {
    const origin = new URL(returnOrigin).origin
    if (!allowedReturnOrigin(origin)) return null
    const candidate = params.get('returnPath')
    const returnPath =
      returnTo === 'photoplan-gallery' &&
      (candidate === `/orders/${bookingId}` || candidate === `/orders/${bookingId}/gallery`)
        ? candidate
        : undefined
    if (returnTo === 'photoplan-gallery' && !returnPath) return null
    return { bookingId, assetId, returnOrigin: origin, returnPath }
  } catch {
    return null
  }
}

export function persistPhotoplanReturnContext(ctx: PhotoplanReturnContext | null): void {
  try {
    if (!ctx) {
      sessionStorage.removeItem(STORAGE_KEY)
      return
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ctx))
  } catch {
    // sessionStorage may be unavailable
  }
}

export function loadPersistedPhotoplanReturnContext(): PhotoplanReturnContext | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PhotoplanReturnContext>
    if (
      typeof parsed.bookingId !== 'string' ||
      typeof parsed.assetId !== 'string' ||
      typeof parsed.returnOrigin !== 'string'
    ) {
      return null
    }
    if (!allowedReturnOrigin(parsed.returnOrigin)) return null
    if (
      parsed.returnPath !== undefined &&
      parsed.returnPath !== `/orders/${parsed.bookingId}` &&
      parsed.returnPath !== `/orders/${parsed.bookingId}/gallery`
    ) {
      return null
    }
    return parsed as PhotoplanReturnContext
  } catch {
    return null
  }
}

export function clearPhotoplanReturnContext(): void {
  persistPhotoplanReturnContext(null)
}

export function sendBackToPhotoplan(ctx: PhotoplanReturnContext, imageUrl: string): void {
  const returnId = crypto.randomUUID()
  const payload = {
    type: STAGING_PHOTOPLAN_RETURN_MESSAGE,
    bookingId: ctx.bookingId,
    assetId: ctx.assetId,
    imageUrl,
    source: 'staging',
    returnId,
  }

  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(payload, ctx.returnOrigin)
    }
  } catch (err) {
    console.warn('[photoplan-return] postMessage failed:', err)
  }

  const params = new URLSearchParams({
    editedImageUrl: imageUrl,
    source: 'staging',
  })
  if (ctx.returnPath) {
    params.set('returnId', returnId)
    params.set('bookingId', ctx.bookingId)
    params.set('assetId', ctx.assetId)
  }
  const returnPath = ctx.returnPath || `/production/photography/${ctx.assetId}`
  const returnUrl = `${ctx.returnOrigin}${returnPath}?${params.toString()}`

  clearPhotoplanReturnContext()

  if (window.opener && !window.opener.closed) {
    try {
      window.opener.location.href = returnUrl
      window.opener.focus()
      try {
        window.close()
      } catch {
        // Popup may not be allowed to close itself.
      }
      return
    } catch (err) {
      console.warn('[photoplan-return] opener navigate failed:', err)
    }
  }

  window.location.href = returnUrl
}
